// FILE: src/services/sessionStartCoordinator.js
//
// Asynchronous session-start coordinator — logic core of PR 2A.
//
// Owns the concurrency contract for starting a learner session now that cert
// banks load lazily (see certBankLoader.ensureCertLoaded). It is framework-
// agnostic and dependency-injected, so the concurrency rules can be unit-tested
// directly without rendering <App/>. App.js instantiates ONE coordinator and
// wires the real dependencies (PR 2A Commit 2); the three session-entry routes
// call begin()/retry() (Commit 2); the rendered controls read a minimal status
// contract (Commit 3).
//
// CONCURRENCY GUARANTEES
//   1. Synchronous in-flight guard — tracked through plain refs/closure
//      variables (NOT React state, which may not have updated between two rapid
//      invocations). begin() is safe to call twice in the same tick.
//   2. Monotonic request token — each begin() takes the next id and becomes the
//      active attempt, preempting any older in-flight attempt. Only the active
//      attempt may mutate anything: start a session, update the question store,
//      set status, set an error, or clear the in-flight flag. A superseded
//      attempt aborts silently the moment it notices it is no longer active.
//      Two immediate starts therefore produce exactly ONE session (the older
//      attempt aborts before it can build a session).
//   3. Stale-certification abort — after the bank resolves we re-read the
//      currently selected certification through getSelectedCertId(). If it no
//      longer matches the attempt's certId, abort silently: no session, no
//      store write, no error surfaced, status returned to idle.
//   4. Retryable failure — on a load error the (serializable) descriptor is
//      preserved via setRetryDescriptor so retry() can replay the exact original
//      configuration. No function closures are stored as retry state.
//   5. An older attempt's settle (its finally) can never clear the in-flight
//      flag belonging to a newer attempt — the flag is only cleared by the
//      attempt that still owns it.
//
// The descriptor is a plain serializable object:
//   { certId, kind, mode, filters?, count?, phases?, phaseLabels? }
// Quarantine ids, exam config, durations and mode labels are derived inside
// buildSession from live app state at build time — they are NOT carried in the
// descriptor, keeping it serializable.
//
// No Redux. Pure async helpers.

/**
 * @typedef {Object} StartDescriptor
 * @property {string}  certId       Certification id to start.
 * @property {string}  kind         'selectMode' | 'targetedTest' | 'targeted'.
 * @property {string}  mode         createSession mode ('practice'|'full'|'targetedTest'|'targeted').
 * @property {Object<string,string[]>} [filters]   Targeted-test filters.
 * @property {number}  [count]      Targeted-test question count.
 * @property {string[]} [phases]    Targeted-quiz phase keys.
 * @property {string}  [phaseLabels] Targeted-quiz human label fragment.
 */

/**
 * @typedef {Object} CoordinatorDeps
 * @property {(certId:string)=>Promise<{questions:any[]}>} ensureCertLoaded
 *           Lazy bank loader; resolves with a CertBankResult (we use .questions).
 *           May resolve a poolOnly (metadata) result when the metadata-pool
 *           flag routes the load — prepareQuestions owns that distinction.
 * @property {()=>string} getSelectedCertId   Reads the CURRENT selected cert (a ref read).
 * @property {(certId:string, questions:any[])=>void} applyLoadedBank
 *           The single owned path that places result.questions into the store.
 *           ONLY invoked on the legacy path (no prepareQuestions dep); when
 *           prepareQuestions is provided it owns ALL store writes itself.
 * @property {(descriptor:StartDescriptor, result:any)=>Promise<any>} [prepareQuestions]
 *           OPTIONAL async draw/hydration step (metadata-pool stage 3). Runs
 *           between the bank/pool load and buildSession; whatever it resolves
 *           is handed to buildSession verbatim. The coordinator re-checks the
 *           supersede + stale-certification guards AFTER it resolves, so a
 *           hydration await can never let an older attempt build a session. A
 *           throw is a retryable load failure (same contract as ensureCertLoaded).
 * @property {(descriptor:StartDescriptor, questions:any[])=>boolean} buildSession
 *           Synchronously builds + starts the session from an EXPLICIT pool.
 *           Returns true if a session was started, false if it declined (e.g. an
 *           empty targeted-test pool).
 * @property {(s:{status:string,certId:(string|null),error:(string|null)})=>void} setStatus
 * @property {(d:(StartDescriptor|null))=>void} setRetryDescriptor
 * @property {(stage:string, descriptor:StartDescriptor, info:Object)=>void} [onLifecycleEvent]
 *           OPTIONAL activation-observability hook (never throws through).
 *           Stages: 'requested' (begin entered), 'built' (session started,
 *           info={duration_ms, load_source, load_status}), 'declined'
 *           (buildSession returned false — e.g. empty targeted pool, same info),
 *           'failed' (retryable load failure surfaced to THIS attempt,
 *           info={duration_ms, message}). Silent aborts (superseded/stale-cert)
 *           deliberately emit nothing — they are concurrency housekeeping,
 *           not learner-visible outcomes.
 */

/**
 * Create a session-start coordinator bound to the given dependencies.
 * @param {CoordinatorDeps} deps
 */
export function createSessionStartCoordinator(deps) {
  const {
    ensureCertLoaded,
    getSelectedCertId,
    applyLoadedBank,
    buildSession,
    setStatus,
    setRetryDescriptor,
    prepareQuestions = null,
    onLifecycleEvent = null,
  } = deps;

  // Activation-observability hook — a listener failure must never break a
  // session start, so every emit is wrapped.
  function emitLifecycle(stage, descriptor, info = {}) {
    try {
      if (typeof onLifecycleEvent === 'function') onLifecycleEvent(stage, descriptor, info);
    } catch (_) { /* observability is side-effect only */ }
  }

  // Synchronous coordination state (closure vars — never React state).
  let requestId = 0; // monotonically increasing; ++ on every begin()
  let activeId = 0;  // id of the attempt currently permitted to mutate
  let inFlight = false;

  const IDLE = { status: 'idle', certId: null, error: null };

  /**
   * Begin (or retry) a session start for the given descriptor.
   * @param {StartDescriptor} descriptor
   * @returns {Promise<boolean>} true if a session was started by THIS attempt.
   */
  async function begin(descriptor) {
    const id = ++requestId; // newer attempts preempt older ones
    activeId = id;
    inFlight = true;
    const certId = descriptor.certId;
    const t0 = Date.now();
    emitLifecycle('requested', descriptor);
    setStatus({ status: 'loading', certId, error: null });

    try {
      const result = await ensureCertLoaded(certId);

      if (id !== activeId) return false;            // superseded → silent abort (#2)
      if (getSelectedCertId() !== certId) {         // stale certification (#3)
        setStatus({ ...IDLE });
        return false;
      }

      let prepared;
      if (typeof prepareQuestions === 'function') {
        // Async draw + hydration (metadata-pool path). prepareQuestions owns
        // the store writes (bank -> questionStores, pool -> metadataPools).
        prepared = await prepareQuestions(descriptor, result);
        // Re-check BOTH guards after the await: a session start issued while
        // hydration was in flight must win, and a cert switch must abort.
        if (id !== activeId) return false;          // superseded → silent abort (#2)
        if (getSelectedCertId() !== certId) {       // stale certification (#3)
          setStatus({ ...IDLE });
          return false;
        }
      } else {
        applyLoadedBank(certId, result.questions);  // single owned store path
        prepared = result.questions;
      }
      const started = buildSession(descriptor, prepared); // explicit pool (#-pool)
      setStatus({ ...IDLE });
      emitLifecycle(started ? 'built' : 'declined', descriptor, {
        duration_ms: Date.now() - t0,
        load_source: (result && result.source) || undefined,
        load_status: (result && result.status) || undefined,
      });
      if (started) setRetryDescriptor(null);
      return started;
    } catch (err) {
      if (id !== activeId) return false;            // superseded → silent abort (#2/#5)
      if (getSelectedCertId() !== certId) {         // cert changed during failure (#3)
        setStatus({ ...IDLE });
        return false;
      }
      setStatus({
        status: 'error',
        certId,
        error: (err && err.message) ? err.message : 'Could not load questions.',
      });
      emitLifecycle('failed', descriptor, {
        duration_ms: Date.now() - t0,
        message: (err && err.message) ? err.message : 'unknown',
      });
      setRetryDescriptor(descriptor);               // serializable retry state (#4)
      return false;
    } finally {
      // Only the still-active attempt may clear the in-flight flag, so an older
      // attempt settling late cannot reopen the gate on a newer one (#5).
      if (id === activeId) inFlight = false;
    }
  }

  return {
    begin,
    /** True while the active attempt is loading. Synchronous read. */
    isInFlight: () => inFlight,
    /** Current monotonic request id (test/introspection). */
    currentRequestId: () => requestId,
  };
}

export default createSessionStartCoordinator;
